import { HttpErrorResponse } from '@angular/common/http';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { LegacyPageEvent as PageEvent } from '@angular/material/legacy-paginator';
import { ConfigurationService } from 'projects/zbs-app/src/app/components/configuration-select/configuration.service';
import { ArticleSearch } from 'projects/zbs-app/src/app/services/Ebootis.Queries/ArticleSearch';
import { ArticleSearchFacets } from 'projects/zbs-app/src/app/services/Ebootis.Queries/ArticleSearchFacets';
import { Configuration } from 'projects/zbs-app/src/app/services/Ebootis.Queries/Configuration';
import { LogisticsAreaType } from 'projects/zbs-app/src/app/services/Ebootis.Shared/LogisticsAreaType';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { LogisticAreaService } from '../../../shared/logistic-area/logistic-area.service';
import { ArticleService } from '../../article.service';
import { ArticleSearchService } from './article-search.service';

@Component({
	selector: 'app-article-search',
	templateUrl: './article-search.component.html',
	styleUrls: ['./article-search.component.scss'],
})
export class ArticleSearchComponent implements OnInit, OnDestroy {

	@Input() public showSelectButton: boolean;
	@Input() public onlyOrderableArticles: boolean;

	@Input() set forCompany(p_forCompany: boolean) {
		this.m_forCompany = p_forCompany;
	}

	get forCompany(): boolean {
		return this.m_forCompany;
	}

	@Output() public articleSelect: EventEmitter<string> = new EventEmitter();
	@Output() public openDetails: EventEmitter<string> = new EventEmitter();

	private m_forCompany: boolean = false;
	public readonly LOGISTIC_AREA_TYPE: LogisticsAreaType = LogisticsAreaType.ARTICLE;
	private readonly LOGISTIC_AREA: string = 'logisticArea';
	private readonly ATTR_1 = 'articleAttribute1';
	private readonly ATTR_2 = 'articleAttribute2';
	private readonly ATTR_3 = 'articleAttribute3';
	private readonly SEARCH_TERM = 'searchTerm';
	public readonly PRICE_MIN = 'priceMin';
	public readonly PRICE_MAX = 'priceMax';
	public readonly CURRENCY = 'currency';
	public m_sortByAttribute: string = '-score,-creationDate';
	private m_subscriptionsSubject = new Subject();
	public m_displayError: HttpErrorResponse;
	public m_messageHandlerClass: string = 'info';
	public m_articleGroups: string[] = [];
	public m_isLoading: boolean = false;
	public m_queryData: ArticleSearch.Result;
	public m_configuration: Configuration.Result;
	public m_attr1: Configuration.Attr1;
	public m_attr2: Configuration.Attr2;
	public m_attr3: Configuration.Attr3;
	public m_attr1Results: string[];
	public m_attr2Results: string[];
	public m_attr3Results: string[];
	public m_paginatorPageSize: number = 50; // Default des Paginators, bei Änderung anpassen!
	public m_paginatorPageIndex: number = 0; // Default des Paginators, bei Änderung anpassen!
	public m_articleSearchForm: UntypedFormGroup;
	public m_lastViewedArticleOid: string;
	/** #11308: VK-Preis steuert ob Felder für Preissuche angezeigt werden */
	public m_showSalesPrice: boolean = false;
	constructor(
		private m_formBuilder: UntypedFormBuilder,
		public m_logisticAreaService: LogisticAreaService,
		public m_articleService: ArticleService,
		public m_articleSearchService: ArticleSearchService,
		private m_configurationService: ConfigurationService,
	) {
		this.m_articleSearchForm = this.m_formBuilder.group({
			[this.LOGISTIC_AREA]: [this.m_logisticAreaService.getLogisticArea(this.LOGISTIC_AREA_TYPE)],
			[this.ATTR_1]: [],
			[this.ATTR_2]: [],
			[this.ATTR_3]: [],
			[this.SEARCH_TERM]: [''],
			[this.PRICE_MIN]: ['', Validators.min(0.00)],
			[this.PRICE_MAX]: ['', Validators.min(0.00)],
			[this.CURRENCY]: ['€'],
		});
	}
	ngOnInit(): void {
		this.m_configurationService.getConfiguration().pipe(
			takeUntil(this.m_subscriptionsSubject),
		).subscribe({
			next: (data) => {
				this.m_configuration = data;
				this.m_attr1 = this.m_configuration?.attr1 ? this.m_configuration.attr1 : undefined;
				this.m_attr2 = this.m_configuration?.attr2 ? this.m_configuration.attr2 : undefined;
				this.m_attr3 = this.m_configuration?.attr3 ? this.m_configuration.attr3 : undefined;
				this.m_showSalesPrice = this.m_configuration?.showSalesPrice;
				this.m_articleSearchForm.get('currency').setValue(this.m_configuration?.currencySymbol);
				this.loadLastSearchValues();
			},
			error: (error) => {
				this.m_displayError = error;
				this.m_messageHandlerClass = 'danger';
			},
		});
	}

	public onLogisticsAreaSet(): void {
		if (this.m_attr1) {
			this.loadArticleAttributes(0);
		}
		this.searchArticle_Submit();
	}

	/**
	 * Setzt die Werte der vorherigen Suche.
	 * Öffnet die zuletzt angesehene Seite der Artikel-Tabelle
	 * und scrollt zum zuletzt angesehenen Artikel. 
	 */
	public loadLastSearchValues(): void {
		const lastSearchParams = this.m_articleSearchService.getLastSearchParams();
		if (lastSearchParams != null) {
			this.m_articleSearchForm.get(this.LOGISTIC_AREA).setValue(lastSearchParams.logisticsAreaProxy);
			this.m_articleSearchForm.get(this.ATTR_1).setValue(lastSearchParams.attrValue1);
			this.m_articleSearchForm.get(this.ATTR_2).setValue(lastSearchParams.attrValue2);
			this.m_articleSearchForm.get(this.ATTR_3).setValue(lastSearchParams.attrValue3);
			this.m_articleSearchForm.get(this.SEARCH_TERM).setValue(lastSearchParams.search);
			this.m_articleSearchForm.get(this.PRICE_MIN).setValue(lastSearchParams.netPriceMin);
			this.m_articleSearchForm.get(this.PRICE_MAX).setValue(lastSearchParams.netPriceMax);
			this.m_paginatorPageSize = lastSearchParams.limit;
			this.m_paginatorPageIndex = lastSearchParams.page;
			this.m_sortByAttribute = lastSearchParams.sortBy;
			this.m_lastViewedArticleOid = this.m_articleSearchService.getLastViewedArticle();
		}
	}
	/**
	 * Die Subscriptions werden geschlossen.
	 */
	public ngOnDestroy(): void {
		this.m_articleSearchService.reset();
		this.m_subscriptionsSubject.next(null);
		this.m_subscriptionsSubject.complete();
	}

	/**
	 * Falls der Logistikbereich geändert wurde, werden die zu Attribut 1 zugehörigen Daten geladen.
	 */
	public logisticAreaChanged(): void {
		this.loadArticleAttributes(0);
	}

	/**
	 * Lädt die Daten zu den zugehören Konfigurationsattribut Gruppen. Wählt neue Felder sowie Felder bei Bedarf ab.
	 * @param p_attributeIndex Index der Attributkategorie
	 * @param p_attributeValue Inhalt der Attributkategorie
	 */
	public loadArticleAttributes(p_attributeIndex: number, p_attributeValue?: string): void {
		const params = new ArticleSearchFacets.Params();
		params.logisticsAreaProxy = this.getControl(this.LOGISTIC_AREA).value ? this.getControl(this.LOGISTIC_AREA).value : null;
		switch (p_attributeIndex) {
			case 0:
				params.attr = this.m_attr1.oid;
				this.getControl(this.ATTR_1).setValue('');
				this.getControl(this.ATTR_2).setValue('');
				this.getControl(this.ATTR_3).setValue('');
				this.m_attr1Results = null;
				this.m_attr2Results = null;
				this.m_attr3Results = null;
				this.m_articleService.searchArticleFacets(params).pipe(
					takeUntil(this.m_subscriptionsSubject),
				).subscribe({
					next: (data) => {
						this.m_attr1Results = data?.searchFacetForAttributeValues;
						if (params.logisticsAreaProxy == null) {
							this.m_attr1Results = null;
							this.m_attr2Results = null;
							this.m_attr3Results = null;
						}
					},
				});
				break;
			case 1:
				params.attr = this.m_attr2.oid;
				if (p_attributeValue) {
					params.attrValue1 = p_attributeValue;
				}
				this.getControl(this.ATTR_2).setValue('');
				this.getControl(this.ATTR_3).setValue('');
				this.m_attr2Results = null;
				this.m_attr3Results = null;
				this.m_articleService.searchArticleFacets(params).pipe(
					takeUntil(this.m_subscriptionsSubject),
				).subscribe({
					next: (data) => {
						this.m_attr2Results = data?.searchFacetForAttributeValues;
						if (p_attributeValue == '') {
							this.m_attr2Results = null;
							this.m_attr3Results = null;
						}
					},
				});
				break;
			case 2:
				params.attr = this.m_attr3.oid;
				if (p_attributeValue) {
					params.attrValue2 = p_attributeValue;
				}
				this.getControl(this.ATTR_3).setValue('');
				this.m_attr3Results = null;
				this.m_articleService.searchArticleFacets(params).pipe(
					takeUntil(this.m_subscriptionsSubject),
				).subscribe({
					next: (data) => {
						this.m_attr3Results = data?.searchFacetForAttributeValues;
						if (p_attributeValue == '') {
							this.m_attr3Results = null;
						}
					},
				});
				break;
		}
	}

	/**
	 * Gibt ein FormControl basierend auf dessen Name zurück.
	 * @param p_name FormControlName
	 */
	public getControl(p_name: string): AbstractControl {
		return this.m_articleSearchForm.get(p_name);
	}

	checkDecimalInput(p_formControl: AbstractControl) {
		if (p_formControl && p_formControl.value) {
			const value: string = p_formControl.value.toString();
			const regex = new RegExp(/[\-\+]/g);
			p_formControl.setValue(value.replace(regex, ''));
		}
	}

	searchArticleFormSubmit_Click() {
		this.m_paginatorPageIndex = 0;
		this.searchArticle_Submit()
	}

	/**
	 * Die Artikelsuche wird ausgeführt. Falls vorhanden werden Suchparameter gesetzt.
	 */
	public searchArticle_Submit(): void {
		this.m_isLoading = true;
		const params = new ArticleSearch.Params();
		params.sortBy = this.m_sortByAttribute;
		params.search = this.getControl('searchTerm').value ? this.getControl('searchTerm').value : undefined;
		params.logisticsAreaProxy = this.getControl('logisticArea').value ? this.getControl('logisticArea').value : undefined;
		params.attrValue1 = this.getControl(this.ATTR_1).value ? this.getControl(this.ATTR_1).value : undefined;
		params.attrValue2 = this.getControl(this.ATTR_2).value ? this.getControl(this.ATTR_2).value : undefined;
		params.attrValue3 = this.getControl(this.ATTR_3).value ? this.getControl(this.ATTR_3).value : undefined;
		params.netPriceMin = this.getControl('priceMin').value >= 0 ? this.getControl('priceMin').value : undefined;
		params.netPriceMax = this.getControl('priceMax').value >= 0 ? this.getControl('priceMax').value : undefined;
		params.page = this.m_paginatorPageIndex ? this.m_paginatorPageIndex : undefined;
		params.limit = this.m_paginatorPageSize ? this.m_paginatorPageSize : undefined;
		params.config = this.m_configurationService.SelectedConfigOid;
		this.m_articleSearchService.searchArticle(params).pipe(
			takeUntil(this.m_subscriptionsSubject),
		).subscribe({
			next: (data) => {
				if (data) {
					this.m_queryData = data;
				}
				this.m_isLoading = false;
				// reload fragmet
			},
			error: (error) => {
				this.m_isLoading = false;
				this.m_displayError = error;
				this.m_messageHandlerClass = 'danger';
			},
		});
	}

	/**
	 * Die Suche wird zurückgesetzt.
	 */
	public resetArticleSearchForm_Click(): void {
		this.m_queryData = undefined;
		this.m_articleSearchService.reset();
		this.m_articleSearchForm.reset();
		this.m_paginatorPageIndex = 0;
		this.m_articleSearchForm.get(this.LOGISTIC_AREA).setValue(this.m_logisticAreaService.getLogisticArea(this.LOGISTIC_AREA_TYPE));
	}

	/**
	 * Setzt FormControl basierend auf dessen Name zurück.
	 * @param p_controlName FormControlName
	 */
	public clearInput_Click(p_controlName): void {
		this.getControl(p_controlName).setValue('');
	}

	/**
	 * Ein ausgewählter Artikel wird an das übergeordenete Component übergeben.
	 * @param p_selectedArticleOid Oid des ausgewählten Artikels
	 */
	public selectArticle(p_selectedArticleOid: string): void {
		this.articleSelect.emit(p_selectedArticleOid);
	}

	/**
	 * Ruft die Artikeldetails auf.
	 * @param p_articleOid Artikel OID
	 */
	public showArticleDetails(p_articleOid: string): void {
		this.m_articleSearchService.setLastViewedArticle(p_articleOid);
		this.openDetails.emit(p_articleOid);
	}

	/**
	 * Ändert die Sortierung der Tabelle
	 * @param sortBy Sortierungsoption
	 */
	public sortingChanged(sortBy: string): void {
		this.m_sortByAttribute = sortBy;
		this.m_paginatorPageIndex = 0;
		this.searchArticle_Submit();
	}

	/**
	 * Eine Aktion des Paginators wurde ausgeführt. Setzt Seitengröße sowie Seitenindex.
	 * @param p_event Event des Paginators.
	 */
	public paginatorPageEvent(p_event: PageEvent): void {
		if (p_event.pageSize != this.m_paginatorPageSize) {
			this.m_paginatorPageSize = p_event.pageSize;
			this.m_paginatorPageIndex = 0;
			this.searchArticle_Submit();
		} else if (p_event.pageIndex != this.m_paginatorPageIndex) {
			this.m_paginatorPageIndex = p_event.pageIndex;
			this.searchArticle_Submit();
		}
	}

	/**
	 * Die Nachrichten des MessageHandlers werden zurückgesetzt.
	 */
	public clearDisplayMessages(): void {
		this.m_displayError = null;
	}

}
